import { useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { errMsg, pay } from "../lib/api";
import { homeFor } from "../components/app/Guard";
import { AuthLayout, AuthField, AuthSubmit, AuthError } from "../components/app/AuthLayout";

const TABS = [["coach", "Coach / team"], ["recruiter", "College recruiter"]];

export default function Join() {
  const { me, ready, signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [kind, setKind] = useState(params.get("type") === "recruiter" ? "recruiter" : "coach");
  const [f, setF] = useState({ name: "", email: params.get("email") ?? "", password: "", team_name: "", org: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const trial = Number(useApi("/plan", pay).data?.trial_days) || 0;

  if (ready && me && !busy) return <Navigate to={homeFor(me)} replace />;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    setError("");
    const base = { name: f.name.trim(), email: f.email.trim(), password: f.password };
    try {
      const user = await signup(kind, kind === "coach" ? { ...base, team_name: f.team_name.trim() } : { ...base, ...(f.org.trim() ? { org: f.org.trim() } : {}) });
      if (!user) throw new Error("Account created. Please log in.");
      nav(kind === "coach" ? "/coach/billing?welcome=1" : homeFor(user), { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <AuthLayout img="/img/team.jpg" alt="High school game in a packed gym" tagline={<>Your program.<br /><span className="text-[#39FF14]">Every</span> number.</>}>
      <form onSubmit={submit} className="space-y-5" data-testid="join-form">
        <div>
          <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Join GAMEDAY</p>
          <h1 className="mt-3 font-display text-5xl font-black uppercase leading-none text-white sm:text-6xl">Join now.</h1>
          <p className="mt-3 text-slate-400">One account works here at HoopsHQ.net and in the GAMEDAY mobile app.</p>
          <Link to="/invite-code" data-testid="join-invite-code-link" className="mt-4 flex items-center justify-between rounded-2xl border border-[#39FF14]/40 bg-[#39FF14]/10 px-4 py-3 text-sm text-white hover:border-[#39FF14]">
            <span><b>Player, parent or assistant coach?</b> Got an invite code from your coach? Enter it here.</span><ArrowRight className="h-4 w-4 shrink-0 text-[#39FF14]" />
          </Link>
        </div>
        <div className="flex gap-1 rounded-full border border-[#1E293B] bg-[#0F172A] p-1">
          {TABS.map(([k, l]) => (
            <button type="button" key={k} data-testid={`join-tab-${k}`} onClick={() => { setKind(k); setError(""); }} className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${kind === k ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <AuthField label="Your name" required data-testid="join-name-input" value={f.name} onChange={set("name")} placeholder="First and last name" autoComplete="name" />
        {kind === "coach"
          ? <AuthField label="Team name" required data-testid="join-team-input" value={f.team_name} onChange={set("team_name")} placeholder="e.g. Central High Varsity" />
          : <AuthField label="School / organisation (optional)" data-testid="join-org-input" value={f.org} onChange={set("org")} placeholder="e.g. State University" />}
        <AuthField label="Email" type="email" required data-testid="join-email-input" value={f.email} onChange={set("email")} placeholder="you@school.edu" autoComplete="email" />
        <AuthField label="Password" type="password" required minLength={8} data-testid="join-password-input" value={f.password} onChange={set("password")} placeholder="At least 8 characters" autoComplete="new-password" />
        {kind === "coach" && <p className="text-sm text-slate-400">Next step: {trial > 0 ? `start your ${trial}-day free trial` : "start your team subscription"} with secure checkout by Stripe.</p>}
        <AuthError error={error} testid="join-error" />
        <AuthSubmit busy={busy} busyLabel="Creating your account…" testid="join-submit-button">
          {kind === "coach" ? "Create team account" : "Create recruiter account"} <ArrowRight className="h-4 w-4" />
        </AuthSubmit>
        <p className="text-sm text-slate-400">Already have an account? <Link to="/login" data-testid="join-login-link" className="font-semibold text-[#39FF14] hover:underline">Log in</Link></p>
      </form>
    </AuthLayout>
  );
}
